"use client"

import { useState } from "react"

import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Music2,
  Trash2,
} from "lucide-react"

import type {
  Playlist,
  Song,
} from "../../metadataTypes"

import { Field } from "./metadataEditorParts"

type Props = {
  playlist: Playlist

  changeSong: (
    index: number,
    patch: Partial<Song>
  ) => void

  removeSong: (
    index: number
  ) => void

  moveSong: (
    index: number,
    direction: -1 | 1
  ) => void
}

export default function MetadataSongs({
  playlist,
  changeSong,
  removeSong,
  moveSong,
}: Props) {
  const [
    expandedSong,
    setExpandedSong,
  ] = useState<string | null>(null)

  return (
    <div className="divide-y divide-white/5">
      {playlist.playlistData.map(
        (song, index) => {
          const expanded =
            expandedSong === song.id

          return (
            <div key={song.id}>

              {/* =================================================
                  FILA COMPACTA
              ================================================= */}

              <div
                onClick={() =>
                  setExpandedSong(
                    expanded
                      ? null
                      : song.id
                  )
                }
                className={`group flex cursor-pointer items-center gap-2 px-2 py-1.5 transition ${
                  expanded
                    ? "bg-white/[.045]"
                    : "hover:bg-white/[.025]"
                }`}
              >

                {/* INDEX */}

                <span className="w-5 shrink-0 text-center font-mono text-[8px] text-white/20">
                  {String(index + 1).padStart(
                    2,
                    "0"
                  )}
                </span>

                {/* COVER */}

                {song.songimg ? (
                  <img
                    src={song.songimg}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/[.05]">
                    <Music2
                      size={12}
                      className="text-white/15"
                    />
                  </div>
                )}

                {/* INFO */}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[10px] text-white/70">
                    {song.songName ||
                      "Sin título"}
                  </p>

                  <p className="truncate text-[8px] text-white/25">
                    {song.songArtist ||
                      "Artista desconocido"}

                    {song.genre
                      ? ` · ${song.genre}`
                      : ""}
                  </p>
                </div>

                {/* ACTIONS */}

                <div className="flex shrink-0 items-center gap-0.5">

                  {/* UP */}

                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()

                      moveSong(
                        index,
                        -1
                      )
                    }}
                    disabled={
                      index === 0
                    }
                    className="rounded-md p-1.5 text-white/20 transition hover:bg-white/[.06] hover:text-white/60 disabled:opacity-10"
                    aria-label="Mover arriba"
                  >
                    <ArrowUp size={11} />
                  </button>

                  {/* DOWN */}

                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()

                      moveSong(
                        index,
                        1
                      )
                    }}
                    disabled={
                      index ===
                      playlist.playlistData
                        .length -
                        1
                    }
                    className="rounded-md p-1.5 text-white/20 transition hover:bg-white/[.06] hover:text-white/60 disabled:opacity-10"
                    aria-label="Mover abajo"
                  >
                    <ArrowDown size={11} />
                  </button>

                  {/* DELETE */}

                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()

                      removeSong(
                        index
                      )
                    }}
                    className="rounded-md p-1.5 text-white/20 transition hover:bg-red-500/10 hover:text-red-300"
                    aria-label="Eliminar canción"
                  >
                    <Trash2 size={11} />
                  </button>

                  {/* EXPAND */}

                  <ChevronDown
                    size={12}
                    className={`ml-1 text-white/20 transition-transform ${
                      expanded
                        ? "rotate-180"
                        : ""
                    }`}
                  />
                </div>
              </div>

              {/* =================================================
                  EDITOR DE CANCIÓN
              ================================================= */}

              {expanded && (
                <div className="grid gap-1.5 border-t border-white/5 bg-black/10 px-2 py-2 sm:grid-cols-2 lg:grid-cols-4">

                  <Field
                    label="Título"
                    value={
                      song.songName
                    }
                    onChange={value =>
                      changeSong(
                        index,
                        {
                          songName:
                            value,
                        }
                      )
                    }
                  />

                  <Field
                    label="Artista"
                    value={
                      song.songArtist
                    }
                    onChange={value =>
                      changeSong(
                        index,
                        {
                          songArtist:
                            value,
                        }
                      )
                    }
                  />

                  <Field
                    label="Género"
                    value={
                      song.genre
                    }
                    onChange={value =>
                      changeSong(
                        index,
                        {
                          genre:
                            value,
                        }
                      )
                    }
                  />

                  <Field
                    label="Link"
                    value={
                      song.link
                    }
                    onChange={value =>
                      changeSong(
                        index,
                        {
                          link:
                            value,
                        }
                      )
                    }
                  />

                  <Field
                    label="Imagen"
                    value={
                      song.songimg
                    }
                    onChange={value =>
                      changeSong(
                        index,
                        {
                          songimg:
                            value,
                        }
                      )
                    }
                    className="sm:col-span-2 lg:col-span-4"
                  />
                </div>
              )}
            </div>
          )
        }
      )}
    </div>
  )
}